import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldAlert } from 'lucide-react';

const STORAGE_KEY = 'careos.medicalDisclaimer.acceptedAt';

export function getDisclaimerAccepted(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

export function setDisclaimerAccepted() {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  } catch {
    /* no-op */
  }
}

interface Props {
  /** Force open (e.g. when re-opened from profile) */
  open?: boolean;
  onClose?: () => void;
}

export function MedicalDisclaimerModal({ open: openProp, onClose }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (openProp !== undefined) {
      setOpen(openProp);
      return;
    }
    if (user && !getDisclaimerAccepted()) {
      setOpen(true);
    }
  }, [user, openProp]);

  const handleAccept = () => {
    setDisclaimerAccepted();
    setOpen(false);
    onClose?.();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // If user is just viewing it from profile, allow closing without accept
      if (openProp !== undefined) {
        setOpen(false);
        onClose?.();
      }
      // Otherwise, force accept
    } else {
      setOpen(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-info" aria-hidden />
            <DialogTitle>{t('medicalDisclaimer.title')}</DialogTitle>
          </div>
          <DialogDescription className="text-sm leading-relaxed pt-2">
            {t('medicalDisclaimer.body')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={handleAccept} className="w-full">
            {t('medicalDisclaimer.agree')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
